import type { ImageProps } from '@_/ui';
import clsx from 'clsx';

interface ImageShape {
  imageUrl: string;
}

interface PostGalleryGridProps<TValue extends ImageShape> {
  children: (data: { imageProps: ImageProps; index: number; item: TValue }) => React.ReactNode;
  items: TValue[];
}

export function PostGalleryGrid<TValue extends ImageShape>({
  children,
  items
}: PostGalleryGridProps<TValue>) {
  const maxHeightClassName = 'max-h-[42rem]';

  if (items.length === 1) {
    return (
      <ul>
        {children({
          imageProps: {
            className: clsx(maxHeightClassName, 'w-full'),
            fit: 'contain',
            src: items[0].imageUrl
          },
          index: 0,
          item: items[0]
        })}
      </ul>
    );
  }

  let gridTemplateAreas: string;
  if (items.length === 2) {
    gridTemplateAreas = `
      'a a a b b b'
    `;
  } else if (items.length === 3) {
    gridTemplateAreas = `
      'a a a a a a'
      'b b b c c c'
    `;
  } else if (items.length === 4) {
    gridTemplateAreas = `
      'a a a b b b'
      'c c c d d d'
    `;
  } else {
    gridTemplateAreas = `
      'a a a b b b'
      'c c d d e e'
    `;
  }

  return (
    <ul className="grid grid-cols-6 gap-1" style={{ gridTemplateAreas }}>
      {items.slice(0, 5).map((item, index) => {
        const fit = index === 0 && items.length === 1 ? 'contain' : 'cover';
        return (
          <li
            key={item.imageUrl}
            className="relative select-none"
            style={{ gridArea: 'abcde'.substring(index, index + 1) }}
          >
            {children({
              imageProps: {
                className: clsx(
                  maxHeightClassName,
                  'w-full',
                  fit === 'cover' && 'h-full',
                  fit === 'cover' && index === 0 && 'aspect-3/2',
                  fit === 'cover' && index !== 0 && 'aspect-square'
                ),
                fit,
                src: item.imageUrl
              },
              index,
              item
            })}

            {index === 4 && items.length > 5 && (
              <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center bg-black/60 text-2xl font-semibold text-white">
                +{items.length - 5}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
